/**
 * PayerSelector Component
 *
 * Compact Autocomplete dropdown for picking a payer.
 * Replaces the chip bar which didn't scale with many payers.
 */

import React from 'react';
import { Autocomplete, Box, TextField, Typography, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import type { PayerContract } from '../types';

interface PayerChipBarProps {
  payers: PayerContract[];
  selectedPayerId: string | null;
  onSelectPayer: (payerId: string | null) => void;
}

export const PayerChipBar: React.FC<PayerChipBarProps> = ({
  payers,
  selectedPayerId,
  onSelectPayer,
}) => {
  const theme = useTheme();

  // When a payer is selected, show a back link + payer name instead of the dropdown
  if (selectedPayerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        <Box
          onClick={() => onSelectPayer(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ArrowBack sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            All Payers
          </Typography>
        </Box>
      </Box>
    );
  }

  // Overview mode: show search dropdown
  return (
    <Box sx={{ mb: 2, maxWidth: 400 }}>
      <Autocomplete
        size="small"
        options={payers}
        getOptionLabel={(option) => option.payer.name}
        renderOption={(props, option) => (
          <li {...props} key={option.payer.name}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.payer.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.payer.payer_type.replace(/_/g, ' ')} &middot; {option.payer.state}
              </Typography>
            </Box>
          </li>
        )}
        onChange={(_, value) => {
          if (value) onSelectPayer(value.payer.name);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search payers..."
            variant="outlined"
          />
        )}
        blurOnSelect
      />
    </Box>
  );
};
